import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Star, MapPin, Sparkles, TrendingUp, ChefHat, Utensils } from "lucide-react";
import { API } from "@/App";

const RecommendationsSection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecommendations();
  }, []);

  const fetchRecommendations = async () => {
    try {
      const response = await axios.get(`${API}/recommendations`, { withCredentials: true });
      setRecommendations(response.data.recommendations || []);
      setInsights(response.data.insights);
    } catch (error) {
      console.warn("Error fetching recommendations:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="py-8">
          <div className="flex justify-center">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (recommendations.length === 0) {
    return (
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            {t('recommendations.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center py-6">
          <Utensils className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground mb-4">{t('recommendations.noRecommendations')}</p>
          <Button 
            onClick={() => navigate('/restaurants')}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {t('recommendations.exploreNow')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="recommendations-section">
      {/* Dining Profile Insights */}
      {insights && insights.total_visits > 0 && (
        <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-primary" />
              {t('recommendations.insights.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              {insights.favorite_cuisine && (
                <div>
                  <p className="text-muted-foreground text-xs">{t('recommendations.insights.favoriteCuisine')}</p>
                  <p className="font-medium">{insights.favorite_cuisine}</p>
                </div>
              )}
              {insights.favorite_country && (
                <div>
                  <p className="text-muted-foreground text-xs">{t('recommendations.insights.favoriteCountry')}</p>
                  <p className="font-medium">{insights.favorite_country}</p>
                </div>
              )}
              {insights.avg_rating_food && (
                <div>
                  <p className="text-muted-foreground text-xs">{t('recommendations.insights.avgFood')}</p>
                  <p className="font-medium flex items-center gap-1">
                    {insights.avg_rating_food}
                    <Star className="w-3 h-3 text-primary fill-primary" />
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recommendations */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            {t('recommendations.title')}
          </CardTitle>
          <p className="text-sm text-muted-foreground">{t('recommendations.subtitle')}</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {recommendations.slice(0, 10).map((restaurant) => (
              <div
                key={restaurant.restaurant_id}
                className="p-3 bg-muted/20 rounded-lg hover:bg-muted/30 transition-colors cursor-pointer group"
                onClick={() => navigate('/restaurants')}
                data-testid={`recommendation-${restaurant.restaurant_id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-sm group-hover:text-primary transition-colors">
                        {restaurant.name}
                      </h4>
                      <div className="flex gap-0.5">
                        {[...Array(restaurant.stars)].map((_, i) => (
                          <Star key={`rec-star-${restaurant.restaurant_id}-${i}`} className="w-3 h-3 text-primary fill-primary" />
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <MapPin className="w-3 h-3" />
                      {restaurant.location}, {restaurant.country}
                    </p>
                    {restaurant.reasons && restaurant.reasons.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {restaurant.reasons.map((reason) => (
                          <span
                            key={`reason-${restaurant.restaurant_id}-${reason}`}
                            className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full"
                          >
                            {reason}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <ChefHat className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RecommendationsSection;
